import { useState, useEffect, useCallback } from 'react';
import { rmmAPI, usersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, HardDriveDownload, Plus, Trash2, ShieldCheck, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { fmtWhen } from './rmmUtils';

const err = (e, m) => toast.error(e.response?.data?.detail || m);
const Spin = () => <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;
const StatusBadge = ({ s }) => (
  <Badge className={s === 'success' ? 'bg-emerald-100 text-emerald-700' : s === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{s}</Badge>
);

export const BackupsTab = ({ isAdmin }) => {
  const [summary, setSummary] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [sources, setSources] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pForm, setPForm] = useState({ name: '', level: 'customer', target_id: '', expected_interval_hours: 24, grace_hours: 6 });
  const [sForm, setSForm] = useState({ name: '', vendor: 'generic' });
  const [newKey, setNewKey] = useState(null);

  const load = useCallback(() => {
    Promise.all([rmmAPI.backupSummary(), rmmAPI.backupPolicies(), rmmAPI.backupSources()])
      .then(([s, p, src]) => { setSummary(s.data); setPolicies(p.data); setSources(src.data); })
      .catch(() => setSummary({}));
  }, []);
  useEffect(() => { load(); usersAPI.list('customer').then((r) => setCustomers(r.data)).catch(() => {}); }, [load]);

  if (!summary) return <Spin />;

  return (
    <div className="space-y-6" data-testid="rmm-backups-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[['Protected devices', summary.protected, 'backup-stat-protected'], ['Unprotected', summary.unprotected, 'backup-stat-unprotected'],
          ['Jobs (30d)', summary.jobs_30d, 'backup-stat-jobs'], ['Successful', summary.success_30d, 'backup-stat-success'],
          ['Failed', summary.failed_30d, 'backup-stat-failed']].map(([l, v, t]) => (
          <Card key={t} className="bg-white border-slate-200" data-testid={t}>
            <CardContent className="p-4"><p className="text-2xl font-bold text-slate-800">{v ?? 0}</p><p className="text-xs text-slate-500">{l}</p></CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><HardDriveDownload className="w-4 h-4 text-cyan-500" />Recent Backup Jobs</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 max-h-96 overflow-y-auto" data-testid="backup-jobs-list">
            {(summary.recent_jobs || []).length === 0 ? <p className="text-sm text-slate-500 py-2 text-center">No backup jobs ingested yet.</p> :
              summary.recent_jobs.map((j) => (
                <div key={j.id} className="flex items-center justify-between text-sm p-2 rounded-lg border border-slate-200">
                  <div><span className="font-medium text-slate-800">{j.device_hostname}</span>
                    <span className="text-xs text-slate-500 ml-2">{j.job_name} · {j.source}{j.size_gb ? ` · ${j.size_gb} GB` : ''}</span></div>
                  <div className="flex items-center gap-2"><StatusBadge s={j.status} /><span className="text-xs text-slate-400">{fmtWhen(j.completed_at)}</span></div>
                </div>))}
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-base">Coverage & Stale Backups</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5" data-testid="backup-coverage">
              {(summary.coverage_by_customer || []).map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm p-2 rounded-lg border border-slate-200">
                  <span className="text-slate-800">{c.customer_name}</span>
                  <span className="text-xs text-slate-500">{c.protected}/{c.total} protected{c.stale ? ` · ${c.stale} stale` : ''}</span>
                </div>))}
            </div>
            {(summary.stale_devices || []).length > 0 && (
              <div className="p-2.5 rounded-lg border border-amber-200 bg-amber-50/50" data-testid="backup-stale-list">
                <p className="text-xs font-semibold text-amber-700 mb-1">Stale backups</p>
                {summary.stale_devices.map((d, i) => (
                  <p key={i} className="text-xs text-slate-600">{d.hostname} · last success {d.last_success ? fmtWhen(d.last_success) : 'never'}</p>))}
              </div>)}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-500" />Backup Policies</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {isAdmin && (
              <div className="p-3 rounded-xl border border-cyan-200 bg-cyan-50/40 grid grid-cols-2 md:grid-cols-5 gap-2 items-center">
                <Input placeholder="Policy name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="bg-white" data-testid="bkp-policy-name" />
                <Select value={pForm.level} onValueChange={(v) => setPForm({ ...pForm, level: v, target_id: '' })}>
                  <SelectTrigger className="bg-white" data-testid="bkp-policy-level"><SelectValue /></SelectTrigger>
                  <SelectContent>{['global', 'customer'].map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent>
                </Select>
                {pForm.level === 'customer' && (
                  <Select value={pForm.target_id} onValueChange={(v) => setPForm({ ...pForm, target_id: v })}>
                    <SelectTrigger className="bg-white" data-testid="bkp-policy-target"><SelectValue placeholder="Customer" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>)}
                <Input type="number" placeholder="Interval (h)" value={pForm.expected_interval_hours} onChange={(e) => setPForm({ ...pForm, expected_interval_hours: parseInt(e.target.value || 24, 10) })} className="bg-white" data-testid="bkp-policy-interval" />
                <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" data-testid="bkp-policy-add-btn"
                  onClick={() => rmmAPI.createBackupPolicy(pForm).then(() => { toast.success('Policy created'); load(); }).catch((e) => err(e, 'Failed'))}><Plus className="w-4 h-4" /></Button>
              </div>)}
            {policies.map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm p-2 rounded-lg border border-slate-200" data-testid={`bkp-policy-${p.name}`}>
                <span className="text-slate-800 font-medium">{p.name}</span>
                <span className="text-xs text-slate-500">{p.level} · every {p.expected_interval_hours}h (+{p.grace_hours}h grace)</span>
                {isAdmin && <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" onClick={() => rmmAPI.deleteBackupPolicy(p.id).then(load)}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>))}
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4 text-cyan-500" />Backup Integrations (adapters)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {isAdmin && (
              <div className="p-3 rounded-xl border border-cyan-200 bg-cyan-50/40 flex gap-2 items-center">
                <Input placeholder="Source name (e.g. Veeam HQ)" value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} className="bg-white" data-testid="bkp-source-name" />
                <Input placeholder="Vendor" value={sForm.vendor} onChange={(e) => setSForm({ ...sForm, vendor: e.target.value })} className="bg-white w-32" data-testid="bkp-source-vendor" />
                <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" data-testid="bkp-source-add-btn"
                  onClick={() => rmmAPI.createBackupSource(sForm).then((r) => { setNewKey(r.data.key); load(); }).catch((e) => err(e, 'Failed'))}><Plus className="w-4 h-4" /></Button>
              </div>)}
            {newKey && <div className="p-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-xs font-mono break-all" data-testid="bkp-source-newkey">Ingest key (shown once): {newKey}<br /><span className="font-sans text-slate-500">POST /api/rmm/backup/ingest with header X-Backup-Key</span></div>}
            {sources.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm p-2 rounded-lg border border-slate-200">
                <span className="text-slate-800">{s.name} <span className="text-xs text-slate-500">({s.vendor})</span></span>
                <span className="text-xs font-mono text-slate-400">{s.key_preview}</span>
              </div>))}
            <p className="text-[11px] text-slate-500">Vendor-neutral: point any backup product (Veeam, Datto, Acronis…) at the ingest API with its source key.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
